<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';
import InboxesAPI from 'dashboard/api/inboxes';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import WhatsAppPhonePreview from './WhatsAppPhonePreview.vue';

const props = defineProps({
  campaign: { type: Object, required: true },
});

const emit = defineEmits(['updated']);

const SHORTENERS =
  /bit\.ly|tinyurl\.com|wa\.me|t\.co|goo\.gl|ow\.ly|is\.gd|buff\.ly|cutt\.ly/i;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const labels = useMapGetter('labels/getLabels');
const lists = useMapGetter('contactLists/getLists');
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getTemplates = useMapGetter('inboxes/getFilteredWhatsAppTemplates');

const templateParserRef = ref(null);
const isSaving = ref(false);
const isAsking = ref(false);
const isSending = ref(false);
const isChecking = ref(false);
const reachCount = ref(null);

const state = reactive({
  title: '',
  inboxId: null,
  mode: 'new',
  templateId: null,
  category: 'MARKETING',
  language: 'es',
  headerType: 'none',
  headerText: '',
  headerMediaUrl: '',
  body: '',
  examples: {},
  footer: '',
  buttonMode: 'none',
  buttons: [],
  includeLabelIds: [],
  excludeLabelIds: [],
  includeListIds: [],
  excludeListIds: [],
  sendNow: true,
  scheduledAt: '',
  templateName: '',
  templateStatus: '',
  rejectedReason: '',
  namespace: '',
});

const templateStatus = computed(() =>
  String(state.templateStatus || '').toUpperCase()
);
const messageLocked = computed(
  () =>
    state.mode === 'existing' ||
    ['PENDING', 'APPROVED'].includes(templateStatus.value)
);
const modeLocked = computed(
  () =>
    state.mode === 'new' &&
    ['PENDING', 'APPROVED'].includes(templateStatus.value)
);

const mapOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const inboxOptions = computed(() => mapOptions(inboxes.value, 'id', 'name'));
const labelOptions = computed(() => mapOptions(labels.value, 'id', 'title'));
const listOptions = computed(() => mapOptions(lists.value, 'id', 'name'));

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  return getTemplates.value(state.inboxId).map(template => ({
    value: template.id,
    label: template.name.replace(/_/g, ' '),
    template,
  }));
});

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(option => option.value === state.templateId)
      ?.template
);
const isMarketing = computed(() => {
  if (state.mode === 'existing')
    return selectedTemplate.value?.category === 'MARKETING';
  return state.category === 'MARKETING';
});

const variableNumbers = computed(() => {
  const found = state.body.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(found.map(item => item.replace(/\D/g, '')))].sort(
    (left, right) => Number(left) - Number(right)
  );
});

const renderedBody = computed(() => {
  let text = state.body;
  variableNumbers.value.forEach(number => {
    const value = String(state.examples[number] || '').trim();
    text = text.replaceAll(`{{${number}}}`, value || `{{${number}}}`);
  });
  return text;
});

const choiceClass = active =>
  `px-3 py-2 text-sm text-left rounded-lg outline outline-1 ${
    active
      ? 'outline-n-brand bg-n-alpha-2 text-n-slate-12'
      : 'outline-n-container text-n-slate-11'
  }`;

const bodyIssue = computed(() => {
  if (messageLocked.value) return '';
  const text = state.body.trim();
  if (!text) return t('CAMPAIGN.WHATSAPP.WORKSPACE.NEED_BODY');
  if (/\{\{\s*[a-zA-Z]/.test(text))
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_LIQUID');
  if (/^\{\{\d+\}\}/.test(text) || /\{\{\d+\}\}$/.test(text)) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_EDGE');
  }
  if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(state.body))
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_PAIR');
  const numbers = variableNumbers.value.map(Number);
  if (numbers.length && numbers.some((number, index) => number !== index + 1)) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_ORDER');
  }
  if (numbers.some(number => !String(state.examples[number] || '').trim())) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_EXAMPLE');
  }
  if (numbers.some(number => /[#$%]/.test(state.examples[number] || ''))) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_SPECIAL');
  }
  return '';
});

const headerIssue = computed(() => {
  if (messageLocked.value || state.headerType === 'none') return '';
  if (state.headerType === 'text') {
    const text = state.headerText;
    if (!text.trim() || text.length > 45 || /[\r\n]/.test(text)) {
      return t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_RULE');
    }
  }
  if (state.headerType === 'image' && !state.headerMediaUrl) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_IMAGE_RULE');
  }
  return '';
});

const buttonIssue = computed(() => {
  if (messageLocked.value || state.buttonMode === 'none') return '';
  const filled = state.buttons.filter(
    button => button.text.trim() || button.url.trim() || button.phone.trim()
  );
  if (!filled.length) return '';
  if (filled.some(button => !button.text.trim())) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_TEXT');
  }
  if (filled.some(button => button.text.trim().length > 25)) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_LENGTH');
  }
  if (state.buttonMode === 'reply') return '';
  if (filled.filter(button => button.kind === 'PHONE_NUMBER').length > 1) {
    return t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_ONE_PHONE');
  }
  const badUrl = filled.some(
    button =>
      button.kind === 'URL' &&
      (!button.url.startsWith('https://') || SHORTENERS.test(button.url))
  );
  if (badUrl) return t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_URL');
  const badPhone = filled.some(
    button =>
      button.kind === 'PHONE_NUMBER' &&
      !/^\+[1-9]\d{7,14}$/.test(button.phone.trim())
  );
  if (badPhone) return t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_PHONE');
  return '';
});

const canAsk = computed(
  () =>
    state.mode === 'new' &&
    !messageLocked.value &&
    !bodyIssue.value &&
    !headerIssue.value &&
    !buttonIssue.value
);
const hasAudience = computed(
  () => state.includeLabelIds.length > 0 || state.includeListIds.length > 0
);

const previewBody = computed(() => {
  if (state.mode !== 'existing') return renderedBody.value;
  const body = selectedTemplate.value?.components?.find(
    item => item.type === 'BODY'
  );
  return body?.text || '';
});

const previewHeader = computed(() => {
  if (state.mode !== 'existing') {
    return {
      type: state.headerType,
      text: state.headerText,
      image: state.headerMediaUrl,
    };
  }
  const header = selectedTemplate.value?.components?.find(
    item => item.type === 'HEADER'
  );
  const format = String(header?.format || '').toUpperCase();
  let type = 'none';
  if (format === 'IMAGE') type = 'image';
  if (format === 'TEXT') type = 'text';
  return {
    type,
    text: header?.text || '',
    image: templateParserRef.value?.processedParams?.header?.media_url || '',
  };
});

const previewFooter = computed(() => {
  if (state.mode !== 'existing') return state.footer;
  return (
    selectedTemplate.value?.components?.find(item => item.type === 'FOOTER')
      ?.text || ''
  );
});

const currentDateTime = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});

const toLocalInput = unix => {
  const date = new Date(unix * 1000);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const exampleList = () =>
  variableNumbers.value.map(number => state.examples[number] || '');

const emptyButton = () => ({ text: '', url: '', phone: '', kind: 'URL' });

const setButtonMode = mode => {
  state.buttonMode = mode;
  if (mode === 'none') {
    state.buttons = [];
    return;
  }
  if (!state.buttons.length) state.buttons = [emptyButton()];
};

const addButton = () => {
  const max = state.buttonMode === 'reply' ? 3 : 2;
  if (state.buttons.length >= max) return;
  state.buttons.push(emptyButton());
};

const removeButton = index => {
  state.buttons.splice(index, 1);
  if (!state.buttons.length) state.buttonMode = 'none';
};

const buttonPayload = () => {
  if (state.buttonMode === 'none') return [];
  return state.buttons
    .filter(button => button.text.trim())
    .map(button => ({
      type: state.buttonMode === 'reply' ? 'QUICK_REPLY' : button.kind,
      text: button.text.trim(),
      url: button.url.trim(),
      phone: button.phone.trim(),
    }));
};

const previewButtons = computed(() => {
  if (state.mode !== 'existing') return buttonPayload();
  const component = selectedTemplate.value?.components?.find(
    item => item.type === 'BUTTONS'
  );
  return (component?.buttons || []).map(button => ({
    type: String(button.type || '').toUpperCase(),
    text: button.text || '',
  }));
});

const processedParams = () => {
  if (state.mode === 'existing')
    return templateParserRef.value?.processedParams || {};
  const params = {};
  const body = {};
  variableNumbers.value.forEach(number => {
    body[number] = state.examples[number] || '';
  });
  if (Object.keys(body).length) params.body = body;
  if (state.headerType === 'image' && state.headerMediaUrl) {
    params.header = { media_url: state.headerMediaUrl, media_type: 'image' };
  }
  return params;
};

const campaignPayload = () => ({
  title: state.title.trim(),
  message: previewBody.value || state.title.trim(),
  inbox_id: state.inboxId,
  scheduled_at:
    state.sendNow || !state.scheduledAt
      ? null
      : new Date(state.scheduledAt).toISOString(),
  audience: {
    include_label_ids: state.includeLabelIds,
    exclude_label_ids: state.excludeLabelIds,
    include_list_ids: state.includeListIds,
    exclude_list_ids: state.excludeListIds,
  },
  template_params: {
    mode: state.mode,
    name: state.templateName,
    namespace: state.namespace,
    category:
      state.mode === 'existing'
        ? selectedTemplate.value?.category
        : state.category,
    language:
      state.mode === 'existing'
        ? selectedTemplate.value?.language
        : state.language,
    status: state.templateStatus,
    rejected_reason: state.rejectedReason,
    header_type: state.mode === 'new' ? state.headerType : 'none',
    header_text: state.mode === 'new' ? state.headerText : '',
    header_media_url: state.mode === 'new' ? state.headerMediaUrl : '',
    body: state.body,
    examples: exampleList(),
    footer: state.footer,
    buttons: buttonPayload(),
    processed_params: processedParams(),
  },
});

const hydrate = campaign => {
  const params = campaign.template_params || {};
  const audience =
    campaign.audience && !Array.isArray(campaign.audience)
      ? campaign.audience
      : {};
  state.title = campaign.title || '';
  state.inboxId = campaign.inbox?.id || null;
  state.mode = params.mode === 'existing' ? 'existing' : 'new';
  state.category = params.category === 'UTILITY' ? 'UTILITY' : 'MARKETING';
  state.language = params.language || 'es';
  state.headerType = params.header_type || 'none';
  state.headerText = params.header_text || '';
  state.headerMediaUrl = params.header_media_url || '';
  state.body = params.body || '';
  state.footer = params.footer || '';
  const storedButtons = Array.isArray(params.buttons) ? params.buttons : [];
  const legacy = params.button?.text ? [params.button] : [];
  const source = storedButtons.length ? storedButtons : legacy;
  if (!source.length) {
    state.buttonMode = 'none';
    state.buttons = [];
  } else {
    const firstType = String(source[0].type || '').toUpperCase();
    state.buttonMode =
      firstType === 'QUICK_REPLY' || (!firstType && !source[0].url)
        ? 'reply'
        : 'action';
    state.buttons = source.map(button => ({
      text: button.text || '',
      url: button.url || '',
      phone: button.phone || button.phone_number || '',
      kind:
        String(button.type || '').toUpperCase() === 'PHONE_NUMBER'
          ? 'PHONE_NUMBER'
          : 'URL',
    }));
  }
  state.templateName = params.name || '';
  state.templateStatus = params.status || '';
  state.rejectedReason = params.rejected_reason || '';
  state.namespace = params.namespace || '';
  state.includeLabelIds = audience.include_label_ids || [];
  state.excludeLabelIds = audience.exclude_label_ids || [];
  state.includeListIds = audience.include_list_ids || [];
  state.excludeListIds = audience.exclude_list_ids || [];
  const examples = {};
  (params.examples || []).forEach((value, index) => {
    examples[String(index + 1)] = value;
  });
  state.examples = examples;
  if (
    campaign.scheduled_at &&
    campaign.scheduled_at * 1000 > Date.now() + 60000
  ) {
    state.sendNow = false;
    state.scheduledAt = toLocalInput(campaign.scheduled_at);
  }
};

const chooseNew = () => {
  if (modeLocked.value) return;
  if (state.mode === 'existing') {
    state.templateName = '';
    state.templateStatus = '';
    state.templateId = null;
    state.rejectedReason = '';
  }
  state.mode = 'new';
};

const insertVariable = () => {
  const next = variableNumbers.value.length
    ? Math.max(...variableNumbers.value.map(Number)) + 1
    : 1;
  const spacer = state.body && !state.body.endsWith(' ') ? ' ' : '';
  state.body = `${state.body}${spacer}{{${next}}} `;
};

const onImage = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  const allowed =
    ['image/jpeg', 'image/png'].includes(file.type) &&
    file.size <= MAX_IMAGE_BYTES;
  if (!allowed) {
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_IMAGE_RULE'));
    return;
  }
  try {
    const uploaded = await uploadFile(file);
    const url = uploaded.fileUrl || '';
    state.headerMediaUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url}`;
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE'));
  }
};

const persist = async () => {
  const response = await CampaignsAPI.update(
    props.campaign.id,
    campaignPayload()
  );
  emit('updated', response.data);
  return response.data;
};

const save = async () => {
  if (!state.title.trim()) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    await persist();
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  } finally {
    isSaving.value = false;
  }
};

const askApproval = async () => {
  if (!canAsk.value || !state.inboxId) return;
  isAsking.value = true;
  try {
    await persist();
    const response = await InboxesAPI.createWhatsappTemplate(state.inboxId, {
      title: state.title,
      language: state.language,
      category: state.category,
      body: state.body,
      footer: state.footer,
      examples: exampleList(),
      buttons: buttonPayload(),
      header_type: state.headerType,
      header_text: state.headerText,
      header_media_url: state.headerMediaUrl,
    });
    const template = response.data.template;
    state.templateName = template.name;
    state.templateStatus = template.status;
    state.rejectedReason = template.rejected_reason || '';
    state.namespace = template.namespace || '';
    state.language = template.language || state.language;
    state.category = template.category || state.category;
    await persist();
    await store.dispatch('inboxes/get');
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.ASKED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  } finally {
    isAsking.value = false;
  }
};

const checkApproval = async () => {
  isChecking.value = true;
  try {
    const response = await CampaignsAPI.refreshTemplate(props.campaign.id);
    hydrate(response.data);
    emit('updated', response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  } finally {
    isChecking.value = false;
  }
};

const existingMessageReady = () => {
  if (state.mode !== 'existing') return true;
  if (!selectedTemplate.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.ERROR'));
    return false;
  }
  const header = selectedTemplate.value.components?.find(
    item => item.type === 'HEADER'
  );
  const format = String(header?.format || '').toUpperCase();
  const params = templateParserRef.value?.processedParams || {};
  const needsFile = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format);
  if (needsFile && !params.header?.media_url) {
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_IMAGE_RULE'));
    return false;
  }
  const bodyText =
    selectedTemplate.value.components?.find(item => item.type === 'BODY')
      ?.text || '';
  const values = Object.values(params.body || {});
  const missingExample =
    /\{\{/.test(bodyText) &&
    (!values.length || values.some(value => !String(value || '').trim()));
  if (missingExample) {
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.BODY_EXAMPLE'));
    return false;
  }
  return true;
};

const send = async () => {
  if (!hasAudience.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.WORKSPACE.NEED_AUDIENCE'));
    return;
  }
  if (!existingMessageReady()) return;
  isSending.value = true;
  try {
    await persist();
    const response = await CampaignsAPI.launch(props.campaign.id, {
      scheduled_at:
        state.sendNow || !state.scheduledAt
          ? null
          : new Date(state.scheduledAt).toISOString(),
    });
    emit('updated', response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE')
    );
  } finally {
    isSending.value = false;
  }
};

const duplicate = async () => {
  try {
    const created = await store.dispatch('campaigns/create', {
      title: t('CAMPAIGN.WHATSAPP.WORKSPACE.COPY_OF', { title: state.title }),
      message: state.body || state.title,
      inbox_id: state.inboxId,
      audience: campaignPayload().audience,
      template_params: {
        mode: 'new',
        category: state.category,
        language: state.language,
        header_type: state.headerType,
        header_text: state.headerText,
        header_media_url: state.headerMediaUrl,
        body: state.body,
        examples: exampleList(),
        footer: state.footer,
        buttons: buttonPayload(),
      },
    });
    router.push({
      name: 'campaigns_whatsapp_show',
      params: { accountId: route.params.accountId, campaignId: created.id },
    });
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE'));
  }
};

const estimateReach = async () => {
  const response = await CampaignsAPI.audiencePreview({
    include_label_ids: state.includeLabelIds,
    exclude_label_ids: state.excludeLabelIds,
    include_list_ids: state.includeListIds,
    exclude_list_ids: state.excludeListIds,
    category: isMarketing.value ? 'MARKETING' : 'UTILITY',
  });
  reachCount.value = response.data.count;
};

watch(
  () => state.templateId,
  () => {
    if (state.mode !== 'existing' || !selectedTemplate.value) return;
    state.templateName = selectedTemplate.value.name;
    state.templateStatus = selectedTemplate.value.status;
    state.language = selectedTemplate.value.language;
    state.category = selectedTemplate.value.category;
    state.namespace = selectedTemplate.value.namespace || '';
  }
);

watch(templateOptions, () => {
  if (state.mode !== 'existing' || !state.templateName || state.templateId)
    return;
  const match = templateOptions.value.find(
    option => option.template?.name === state.templateName
  );
  if (match) state.templateId = match.value;
});

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('contactLists/get');
  hydrate(props.campaign);
});
</script>

<template>
  <div class="flex flex-col w-full max-w-6xl gap-6 mx-auto">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <Input
        v-model="state.title"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        class="w-full max-w-md"
      />
      <div class="flex flex-wrap gap-2">
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.SAVE')"
          :is-loading="isSaving"
          @click="save"
        />
        <Button
          v-if="canAsk"
          :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.ASK')"
          :is-loading="isAsking"
          @click="askApproval"
        />
        <Button
          v-if="templateStatus === 'PENDING'"
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP.SHOW.CHECK')"
          :is-loading="isChecking"
          @click="checkApproval"
        />
        <Button
          v-if="templateStatus === 'APPROVED'"
          :label="
            state.sendNow
              ? t('CAMPAIGN.WHATSAPP.WORKSPACE.SEND')
              : t('CAMPAIGN.WHATSAPP.WORKSPACE.SCHEDULE')
          "
          :is-loading="isSending"
          @click="send"
        />
      </div>
    </div>

    <p
      v-if="templateStatus === 'PENDING'"
      class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-slate-12"
    >
      {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.LOCKED') }}
    </p>
    <p
      v-else-if="templateStatus === 'REJECTED'"
      class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-slate-12"
    >
      {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.REJECTED_EDIT') }}
      <span v-if="state.rejectedReason">{{ state.rejectedReason }}</span>
    </p>
    <p
      v-else-if="templateStatus === 'APPROVED'"
      class="p-3 text-sm rounded-lg bg-n-teal-3 text-n-slate-12"
    >
      {{ t('CAMPAIGN.WHATSAPP.SHOW.READY') }}
    </p>

    <div
      class="grid items-start grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]"
      :class="messageLocked ? 'order-2' : 'order-1'"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.FROM') }}
          </label>
          <ComboBox
            v-model="state.inboxId"
            :options="inboxOptions"
            :disabled="modeLocked"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
          />
        </div>

        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            :class="choiceClass(state.mode === 'new')"
            :disabled="modeLocked"
            @click="chooseNew"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.WRITE_NEW') }}
          </button>
          <button
            type="button"
            :class="choiceClass(state.mode === 'existing')"
            :disabled="modeLocked"
            @click="state.mode = 'existing'"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.USE_EXISTING') }}
          </button>
        </div>

        <template v-if="state.mode === 'existing'">
          <ComboBox
            v-model="state.templateId"
            :options="templateOptions"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
            "
          />
          <WhatsAppTemplateParser
            v-if="selectedTemplate"
            :key="state.templateId"
            ref="templateParserRef"
            :template="selectedTemplate"
          />
        </template>

        <template v-else>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              :class="choiceClass(state.category === 'MARKETING')"
              :disabled="messageLocked"
              @click="state.category = 'MARKETING'"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.MARKETING') }}
            </button>
            <button
              type="button"
              :class="choiceClass(state.category === 'UTILITY')"
              :disabled="messageLocked"
              @click="state.category = 'UTILITY'"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.UTILITY') }}
            </button>
          </div>
          <p v-if="isMarketing" class="text-xs text-n-amber-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONSENT') }}
          </p>

          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.LANGUAGE') }}
            <select
              v-model="state.language"
              :disabled="messageLocked"
              class="w-full px-3 py-2 mt-1 text-sm rounded-lg bg-n-background outline outline-1 outline-n-container disabled:opacity-50"
            >
              <option value="es">
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.LANG_ES') }}
              </option>
              <option value="en">
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.LANG_EN') }}
              </option>
            </select>
          </label>

          <div class="flex flex-col gap-2">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER') }}
            </p>
            <div class="grid grid-cols-3 gap-2">
              <button
                type="button"
                :class="choiceClass(state.headerType === 'none')"
                :disabled="messageLocked"
                @click="state.headerType = 'none'"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_NONE') }}
              </button>
              <button
                type="button"
                :class="choiceClass(state.headerType === 'text')"
                :disabled="messageLocked"
                @click="state.headerType = 'text'"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_TEXT') }}
              </button>
              <button
                type="button"
                :class="choiceClass(state.headerType === 'image')"
                :disabled="messageLocked"
                @click="state.headerType = 'image'"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_IMAGE') }}
              </button>
            </div>
            <Input
              v-if="state.headerType === 'text'"
              v-model="state.headerText"
              :disabled="messageLocked"
              :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_TEXT_LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.WORKSPACE.HEADER_TEXT_PLACEHOLDER')
              "
              :message="headerIssue"
              :message-type="headerIssue ? 'error' : 'info'"
            />
            <div
              v-else-if="state.headerType === 'image'"
              class="flex flex-col gap-2"
            >
              <input
                type="file"
                accept="image/jpeg,image/png"
                :disabled="messageLocked"
                class="text-sm text-n-slate-11"
                @change="onImage"
              />
              <p class="text-xs text-n-slate-11">
                {{ headerIssue || t('CAMPAIGN.WHATSAPP.WORKSPACE.IMAGE_HELP') }}
              </p>
            </div>
          </div>

          <TextArea
            v-model="state.body"
            :disabled="messageLocked"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.BODY')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.BODY_PLACEHOLDER')
            "
            :max-length="1024"
            :message="bodyIssue"
            :message-type="bodyIssue ? 'error' : 'info'"
            auto-height
          />
          <div class="flex items-center justify-between gap-3">
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.VARIABLE_HELP') }}
            </p>
            <Button
              variant="faded"
              color="slate"
              size="sm"
              type="button"
              :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.INSERT')"
              :disabled="messageLocked"
              @click="insertVariable"
            />
          </div>
          <Input
            v-for="number in variableNumbers"
            :key="number"
            v-model="state.examples[number]"
            :disabled="messageLocked"
            :label="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.EXAMPLE', { number })
            "
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.EXAMPLE_PLACEHOLDER')
            "
          />

          <Input
            v-model="state.footer"
            :disabled="messageLocked"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.FOOTER')"
          />
          <div class="flex flex-col gap-2">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS') }}
            </p>
            <div class="grid grid-cols-3 gap-2">
              <button
                type="button"
                :class="choiceClass(state.buttonMode === 'none')"
                :disabled="messageLocked"
                @click="setButtonMode('none')"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS_NONE') }}
              </button>
              <button
                type="button"
                :class="choiceClass(state.buttonMode === 'reply')"
                :disabled="messageLocked"
                @click="setButtonMode('reply')"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS_REPLY') }}
              </button>
              <button
                type="button"
                :class="choiceClass(state.buttonMode === 'action')"
                :disabled="messageLocked"
                @click="setButtonMode('action')"
              >
                {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS_ACTION') }}
              </button>
            </div>
            <p
              v-if="state.buttonMode !== 'none'"
              class="text-xs text-n-slate-11"
            >
              {{
                state.buttonMode === 'action'
                  ? t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS_ACTION_HELP')
                  : t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTONS_REPLY_HELP')
              }}
            </p>
            <div
              v-for="(button, index) in state.buttons"
              :key="index"
              class="flex flex-col gap-2"
            >
              <Input
                v-model="button.text"
                :disabled="messageLocked"
                :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.BUTTON')"
              />
              <template v-if="state.buttonMode === 'action'">
                <div class="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    :class="choiceClass(button.kind === 'URL')"
                    :disabled="messageLocked"
                    @click="button.kind = 'URL'"
                  >
                    {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_LINK') }}
                  </button>
                  <button
                    type="button"
                    :class="choiceClass(button.kind === 'PHONE_NUMBER')"
                    :disabled="messageLocked"
                    @click="button.kind = 'PHONE_NUMBER'"
                  >
                    {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_CALL') }}
                  </button>
                </div>
                <Input
                  v-if="button.kind === 'URL'"
                  v-model="button.url"
                  :disabled="messageLocked"
                  :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER.BUTTON_URL')"
                  placeholder="https://"
                />
                <Input
                  v-else
                  v-model="button.phone"
                  :disabled="messageLocked"
                  :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_PHONE_LABEL')"
                  placeholder="+57"
                />
              </template>
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_REMOVE')"
                :disabled="messageLocked"
                @click="removeButton(index)"
              />
            </div>
            <p v-if="buttonIssue" class="text-xs text-n-ruby-11">
              {{ buttonIssue }}
            </p>
            <Button
              v-if="
                state.buttonMode !== 'none' &&
                state.buttons.length < (state.buttonMode === 'reply' ? 3 : 2)
              "
              variant="faded"
              color="slate"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.BUTTON_ADD')"
              :disabled="messageLocked"
              @click="addButton"
            />
          </div>
        </template>

        <Button
          v-if="templateStatus === 'APPROVED' && state.mode === 'new'"
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.DUPLICATE')"
          @click="duplicate"
        />
      </div>

      <WhatsAppPhonePreview
        class="xl:sticky xl:top-4"
        :header-type="previewHeader.type"
        :header-text="previewHeader.text"
        :header-image="previewHeader.image"
        :body="previewBody"
        :footer="previewFooter"
        :buttons="previewButtons"
      />
    </div>

    <section
      class="flex flex-col px-4 py-2 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
      :class="messageLocked ? 'order-1' : 'order-2'"
    >
      <div class="py-3">
        <h2 class="text-heading-3 text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.DELIVERY') }}
        </h2>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WORKSPACE.DELIVERY_HELP') }}
        </p>
      </div>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.LISTS.LABEL')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.LISTS_HELP')"
      >
        <TagMultiSelectComboBox
          v-model="state.includeListIds"
          :options="listOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.LISTS.PLACEHOLDER')"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.AUDIENCE_HELP')"
      >
        <TagMultiSelectComboBox
          v-model="state.includeLabelIds"
          :options="labelOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.EXCLUDE_LISTS.LABEL')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.EXCLUDE_LISTS_HELP')"
      >
        <TagMultiSelectComboBox
          v-model="state.excludeListIds"
          :options="listOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.EXCLUDE_LISTS.PLACEHOLDER')
          "
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.EXCLUDE.LABEL')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.EXCLUDE_HELP')"
      >
        <TagMultiSelectComboBox
          v-model="state.excludeLabelIds"
          :options="labelOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.EXCLUDE.PLACEHOLDER')"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.WHEN')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.WHEN_HELP')"
      >
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_NOW') }}
            </span>
            <Switch v-model="state.sendNow" />
          </div>
          <Input
            v-if="!state.sendNow"
            v-model="state.scheduledAt"
            type="datetime-local"
            :min="currentDateTime"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
          />
        </div>
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('CAMPAIGN.WHATSAPP.WORKSPACE.REACH_LABEL')"
        :help-text="t('CAMPAIGN.WHATSAPP.WORKSPACE.REACH_HELP')"
      >
        <div class="flex items-center justify-between gap-3">
          <span
            class="text-sm"
            :class="reachCount === null ? 'text-n-slate-11' : 'text-n-slate-12'"
          >
            {{
              reachCount === null
                ? t('CAMPAIGN.WHATSAPP.WORKSPACE.REACH_EMPTY')
                : t('CAMPAIGN.WHATSAPP.CREATE.FORM.REACH', {
                    count: reachCount,
                  })
            }}
          </span>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.REACH_BUTTON')"
            @click="estimateReach"
          />
        </div>
      </SettingsFieldSection>
    </section>
  </div>
</template>
